import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { Icon } from './Icon.js';
import JellyfishLoader from './JellyfishLoader';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { 
  getBiomarkerSelectFields,
  enrichBiomarkersWithRanges,
  getBiomarkerStatus as getStatus,
  getExtendedRange,
  getUserValuePosition,
  getClinicalRangePosition,
  extractWebsiteName,
  formatBiomarkerValueForDisplay
} from '../utils/biomarkerUtils';
import './AttachmentSelector.css';
import './LabsTab.css';
import './BiomarkerSelector.css';

const BiomarkerSelector = ({ onSelect, onClose, selectedBiomarker = null }) => {
  const [biomarkers, setBiomarkers] = useState([]);
  const [biomarkerResults, setBiomarkerResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [userGender, setUserGender] = useState(null);
  const contentRef = useRef(null);
  const lastTouchY = useRef(null);

  useEffect(() => {
    fetchBiomarkersWithResults();
    
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore body scroll when modal closes
      document.body.style.overflow = '';
    };
  }, []);

  const fetchBiomarkersWithResults = async () => {
    // Deduplicate in-flight requests (e.g. React StrictMode double-mount in dev)
    const cacheKey = getCacheKey('BiomarkerSelector_fetchBiomarkersWithResults', 'v1');
    return dedupeRequest(cacheKey, async () => {
      try {
        setLoading(true);
        // Use getSession() instead of getUser() to avoid network requests
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) throw new Error('Not authenticated');

        // Fetch user profile to get gender - try to reuse cached profile data first
        // Check if we already have profile data cached from Home/TopNavigation
        const profileCacheKey = getCacheKey('fetchUserProfile', user.id);
        let profile = null;
        
        // First, try to get from sessionStorage cache (set by Home.js/TopNavigation.js)
        const cachedProfile = sessionStorage.getItem(`userProfile_${user.id}`);
        if (cachedProfile) {
          try {
            const parsed = JSON.parse(cachedProfile);
            if (parsed.gender !== undefined) {
              profile = { gender: parsed.gender };
            }
          } catch (e) {
            // Invalid cache, continue to fetch
          }
        }
        
        // If not in cache, fetch using the same cache key as Home/TopNavigation
        // This will reuse the in-flight request if Home/TopNavigation already started it
        if (!profile) {
          profile = await dedupeRequest(profileCacheKey, async () => {
            const { data, error } = await supabase
              .from('user_profiles')
              .select('username, profile_picture_url, gender')
              .eq('user_id', user.id)
              .maybeSingle();
            if (error) throw error;
            return data;
          }, { keepAliveMs: 2000, caller: 'BiomarkerSelector' });
        }
        
        if (profile) {
          setUserGender(profile.gender);
        }

        // Fetch all biomarkers
        const { data: biomarkersData, error: biomarkersError } = await supabase
          .from('biomarkers')
          .select(getBiomarkerSelectFields())
          .order('name', { ascending: true });

        if (biomarkersError) throw biomarkersError;

        // Fetch ALL panels (not just latest) so selector shows biomarkers from every panel
        const { data: panelData, error: panelError } = await supabase
          .from('biomarker_panel_results')
          .select(`
            *,
            biomarker_results (
              *,
              biomarkers (*)
            )
          `)
          .eq('user_id', user.id)
          .order('test_date', { ascending: false });

        if (panelError) throw panelError;

        // Flatten biomarker results from ALL panels (so we show biomarkers that have a result in any panel)
        const allResults = [];
        panelData?.forEach(panel => {
          panel.biomarker_results?.forEach(result => {
            allResults.push({
              ...result,
              panel: panel,
              biomarker: result.biomarkers
            });
          });
        });

        // Enrich biomarkers with gender-specific ranges
        const enrichedBiomarkers = enrichBiomarkersWithRanges(biomarkersData || [], profile?.gender);
        
        setBiomarkers(enrichedBiomarkers);
        setBiomarkerResults(allResults);
      } catch (err) {
        console.error('Error fetching biomarkers:', err);
        setError('Failed to load biomarkers');
      } finally {
        setLoading(false);
      }
    });
  };

  const getBiomarkerStatusLocal = (biomarker, result) => {
    if (!result) return 'no-data';
    const value = parseFloat(result.value);
    const status = getStatus(value, biomarker, userGender);
    
    // Map status to the expected format
    if (status === 'critical') return 'out-of-range';
    if (status === 'normal') return 'normal';
    return 'no-data';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'optimal': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'critical': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOverlayWheel = (e) => {
    // Only prevent if the event is directly on the overlay (not bubbling from content)
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleContentWheel = (e) => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // Allow 1px tolerance
    
    // If scrolling up at the top, or scrolling down at the bottom, prevent default
    if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      // Normal scrolling - just prevent bubbling
      e.stopPropagation();
    }
  };

  const handleContentTouchStart = (e) => {
    if (e.touches.length > 0) {
      lastTouchY.current = e.touches[0].clientY;
    }
    e.stopPropagation();
  };

  const handleContentTouchMove = (e) => {
    const content = contentRef.current;
    if (!content) {
      e.stopPropagation();
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = content;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
    
    if (e.touches.length > 0 && lastTouchY.current !== null) {
      const currentTouchY = e.touches[0].clientY;
      const deltaY = currentTouchY - lastTouchY.current;
      lastTouchY.current = currentTouchY;
      
      // If trying to scroll up at top or down at bottom, prevent default
      if ((deltaY > 0 && isAtTop) || (deltaY < 0 && isAtBottom)) {
        e.preventDefault();
        e.stopPropagation();
      } else {
        e.stopPropagation();
      }
    } else {
      e.stopPropagation();
    }
  };

  // One card per result (so user can pick any biomarker from any historical panel)
  const filteredResults = biomarkerResults.filter(r => {
    const biomarker = biomarkers.find(b => b.id === r.biomarker_id);
    if (!biomarker) return false;
    return (
      biomarker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      biomarker.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Sort by test date (most recent first), then by biomarker name
  const sortedResults = [...filteredResults].sort((a, b) => {
    const dateA = new Date(a.panel?.test_date || 0);
    const dateB = new Date(b.panel?.test_date || 0);
    if (dateB - dateA !== 0) return dateB - dateA;
    const nameA = (a.biomarker?.name || '').toLowerCase();
    const nameB = (b.biomarker?.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const handleResultClick = (resultItem) => {
    const biomarker = biomarkers.find(b => b.id === resultItem.biomarker_id);
    if (!biomarker) return;
    const result = resultItem;
    const status = getBiomarkerStatusLocal(biomarker, result);
    const userValuePos = getUserValuePosition(biomarker, result, userGender);
    const clinicalRangePos = getClinicalRangePosition(biomarker, userGender);

    const biomarkerData = {
      ...biomarker,
      result,
      status,
      userValuePosition: userValuePos,
      clinicalRange: clinicalRangePos
    };

    // If clicking the already selected result, deselect it
    const sameResult = selectedBiomarker?.result?.id && result.id && selectedBiomarker.result.id === result.id;
    const sameBiomarkerLegacy = selectedBiomarker?.id === biomarker.id && !selectedBiomarker?.result?.id;
    if (sameResult || sameBiomarkerLegacy) {
      onSelect(null);
    } else {
      onSelect(biomarkerData);
    }
  };

  if (loading) {
    return (
      <div className="attachment-selector-overlay" onClick={onClose}>
        <div className="attachment-selector" onClick={(e) => e.stopPropagation()}>
          <div className="attachment-selector-loading">
            <JellyfishLoader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="attachment-selector-overlay" 
      onClick={handleOverlayClick}
      onWheel={handleOverlayWheel}
      onTouchMove={(e) => {
        // Only prevent if not touching the modal content
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div className="attachment-selector" onClick={(e) => e.stopPropagation()}>
        <div className="attachment-selector-header">
          <div className="attachment-selector-title">
            <Icon name="TestTube" className="icon--sm" />
            <span>Select Biomarker</span>
          </div>
          <div className="attachment-selector-actions">
            <button 
              className="attachment-search-toggle"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Icon name="Search" className="icon--sm" />
            </button>
            <button className="attachment-selector-close" onClick={onClose}>
              <Icon name="X" className="icon--sm" />
            </button>
          </div>
        </div>

        {showSearch && (
          <div className="attachment-selector-search">
            <input
              type="text"
              placeholder="Search biomarkers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="attachment-search-input"
              autoFocus
            />
            <button 
              className="attachment-search-close"
              onClick={() => {
                setShowSearch(false);
                setSearchTerm('');
              }}
            >
              <Icon name="X" className="icon--xs" />
            </button>
          </div>
        )}

        <div 
          ref={contentRef}
          className="attachment-selector-content" 
          onWheel={handleContentWheel} 
          onTouchStart={handleContentTouchStart}
          onTouchMove={handleContentTouchMove}
        >
          {error ? (
            <div className="attachment-selector-error">
              <Icon name="AlertCircle" className="icon--sm" />
              <span>{error}</span>
            </div>
          ) : sortedResults.length === 0 ? (
            <div className="attachment-selector-empty">
              <Icon name="TestTube" className="icon--md" />
              <span>No biomarkers with results found</span>
              <p className="attachment-selector-empty-hint">
                Upload lab results in your Labs tab to see biomarkers here
              </p>
            </div>
          ) : (
            <div className="biomarker-selector-grid">
              {sortedResults.map((resultItem) => {
                const biomarker = biomarkers.find(b => b.id === resultItem.biomarker_id);
                if (!biomarker) return null;
                const result = resultItem;
                const status = getBiomarkerStatusLocal(biomarker, result);
                const userValuePos = getUserValuePosition(biomarker, result, userGender);
                const clinicalRangePos = getClinicalRangePosition(biomarker, userGender);
                const isSelected =
                  (selectedBiomarker?.result?.id && result.id && selectedBiomarker.result.id === result.id) ||
                  (selectedBiomarker?.id === biomarker.id && !selectedBiomarker?.result?.id);
                const displayValue = result ? formatBiomarkerValueForDisplay(result.value) : '';
                const displayUnit = result ? (result.unit_override || biomarker.unit) : biomarker.unit || '';
                const hasResult = !!result;
                const digitCount = String(displayValue).replace(/\D/g, '').length;
                const hasThreeDigitValue = digitCount >= 3;
                const testDate = hasResult && result.panel?.test_date
                  ? new Date(result.panel.test_date).toISOString().split('T')[0]
                  : '';

                const handleCopySource = async (e) => {
                  e.stopPropagation();
                  if (biomarker.description_source) {
                    try {
                      await navigator.clipboard.writeText(biomarker.description_source);
                      const originalText = e.target.textContent;
                      e.target.textContent = 'Copied!';
                      setTimeout(() => {
                        e.target.textContent = originalText;
                      }, 2000);
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }
                };

                const handleCopyRangeSource = async (e) => {
                  e.stopPropagation();
                  if (biomarker.range_source) {
                    try {
                      await navigator.clipboard.writeText(biomarker.range_source);
                      const sourceLink = e.target.closest('.biomarker-value-range-wrapper')?.querySelector('.biomarker-source-link');
                      if (sourceLink) {
                        const originalText = sourceLink.textContent;
                        sourceLink.textContent = 'Copied!';
                        setTimeout(() => {
                          sourceLink.textContent = originalText;
                        }, 2000);
                      }
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }
                };

                return (
                  <div
                    key={result.id}
                    className={`biomarker-selector-card-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleResultClick(resultItem)}
                  >
                    <div 
                      className={`biomarker-card ${!hasResult ? 'no-data' : ''} ${hasThreeDigitValue ? 'biomarker-card--value-three-digits' : ''}`}
                    >
                      <div className="biomarker-card-header">
                        <div className="biomarker-card-title">
                          <Icon name="TestTube" className="biomarker-card-icon" />
                          <h3>
                            {biomarker.name}
                          </h3>
                        </div>
                        {biomarker.description && (
                          <div className="biomarker-card-info-wrapper">
                            <button className="biomarker-card-info" aria-label="Biomarker information" onClick={(e) => e.stopPropagation()}>
                              <Icon name="Info" />
                              <div className="biomarker-card-tooltip">
                                {biomarker.description}
                                {biomarker.description_source && (
                                  <p>
                                    Source:{' '}
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      className="biomarker-source-link"
                                      onClick={handleCopySource}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          handleCopySource(e);
                                        }
                                      }}
                                    >
                                      {extractWebsiteName(biomarker.description_source)}
                                    </span>
                                  </p>
                                )}
                              </div>
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="biomarker-card-content">
                        <div className="biomarker-value">
                          {hasResult && (
                            <>
                              <span className="biomarker-value-number">{displayValue}</span>
                              <span className="biomarker-value-unit">{displayUnit}</span>
                              {(biomarker.clinical_low !== null || biomarker.clinical_high !== null) && (
                                <div className="biomarker-value-range-wrapper">
                                  <span className="biomarker-value-range">
                                    Range: {biomarker.clinical_low !== null ? formatBiomarkerValueForDisplay(biomarker.clinical_low) : '•'}-{biomarker.clinical_high !== null ? formatBiomarkerValueForDisplay(biomarker.clinical_high) : '•'}
                                  </span>
                                  {biomarker.range_source && (
                                    <div className="biomarker-value-range-tooltip">
                                      <p>
                                        Source:{' '}
                                        <span
                                          role="button"
                                          tabIndex={0}
                                          className="biomarker-source-link"
                                          onClick={handleCopyRangeSource}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              handleCopyRangeSource(e);
                                            }
                                          }}
                                        >
                                          {extractWebsiteName(biomarker.range_source)}
                                        </span>
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <div className="biomarker-progress">
                          <div className="biomarker-progress-bar">
                            {/* Clinical range background (supports partial ranges, e.g. high-only like eGFR) */}
                            {(biomarker.clinical_low !== null || biomarker.clinical_high !== null) && (
                              <div 
                                className="biomarker-progress-clinical-range"
                                style={{
                                  left: `${clinicalRangePos.start}%`,
                                  width: `${clinicalRangePos.end - clinicalRangePos.start}%`
                                }}
                              />
                            )}
                            {/* User value vertical line */}
                            {hasResult && userValuePos !== null && (
                              <div 
                                className="biomarker-progress-value-line"
                                style={{
                                  left: `${userValuePos}%`
                                }}
                              />
                            )}
                          </div>
                        </div>

                        <div className="biomarker-divider"></div>

                        <div className="biomarker-tags">
                          {biomarker.category && (
                            <div className="biomarker-category-tag">
                              {biomarker.category}
                            </div>
                          )}
                          {testDate && (
                            <div className="biomarker-test-tag">
                              {testDate}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="biomarker-selector-check-overlay">
                        <Icon name="Check" className="icon--md" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="attachment-selector-footer">
          <div className="attachment-selection-info">
            {selectedBiomarker
              ? `Selected: ${selectedBiomarker.name}${selectedBiomarker.result?.panel?.test_date ? ' (' + new Date(selectedBiomarker.result.panel.test_date).toISOString().split('T')[0] + ')' : ''}`
              : 'No biomarker selected'}
          </div>
          <button
            className="attachment-done-btn"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default BiomarkerSelector;

